import React, { FC, useContext, useEffect, useState } from "react";
import { ClientAutoRedactionState } from "src/common/utils";
import OrganizationAPI from "../API/OrganizationAPI";
import DatasetAPI from "./DatasetAPI";
import { useApi } from "../../../Api/useApi";
import Spinner from "src/components/common/Spinner";
import { ModalOverlay, Page } from "src/components/common";
import { Button, Form, Input, InputNumber, Radio, Select } from "antd";
import { FormComponentProps } from "antd/lib/form";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";
import { AdminPermissions } from "src/common/Permissions";
import CareConfigurationSelector from "src/components/forms/CareConfigurationSelector";
import DatasetContext from "src/context/DatasetContext";
import PickListContext from "src/context/pickListContext";
import {
	DatasetPurposeType,
	Module,
	ModuleDatasetXRef,
} from "src/@types/ImpiricaPlatformApi";
import moment from "moment";
import { toast } from "react-toastify";

interface IProps extends FormComponentProps {
	match?: any;
	history?: any;
}

const defaultDataset: DatasetDTO = {
	id: -1,
	name: "",
	isActive: true,
	training: false,
	automaticClientRedaction: ClientAutoRedactionState.Inherit,
	productId: undefined,
};

const OrganizationDatasetDetails: FC<IProps> = ({
	match,
	history,
	form: {
		getFieldDecorator,
		getFieldsError,
		validateFields,
		setFieldsValue,
		getFieldValue,
	},
}) => {
	const { modulesClient, datasetsClient } = useApi();

	const { pickListItems } = useContext(PickListContext);
	const [dataset, setDataset] = useState<DatasetDTO>(defaultDataset);
	const [organization, setOrganization] = useState<OrganizationDTO>(
		{} as OrganizationDTO
	);
	const [products, setProducts] = useState<ProductDTO[]>([]);
	const [datasetNames, setDatasetNames] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [saving, setSaving] = useState<boolean>(false);
	const [redactMessageOpen, setRedactMessageOpen] = useState<boolean>(false);
	const [redactConfirmationOpen, setRedactConfirmationOpen] =
		useState<boolean>(false);
	const [formIsValid, setFormIsValid] = useState<boolean>(false);
	const [initialModules, setInitialModules] = useState<Module[]>([]);
	const [productModules, setProductModules] = useState<Module[]>([]);
	const [datasetPurposes, setDatasetPurposes] = useState<DatasetPurposeType[]>(
		[]
	);
	const { updateCurrentDataset } = useContext(DatasetContext);
	const editingDataset =
		match.params.datasetId !== undefined && match.params.datasetId > 0;

	useEffect(() => {
		const {
			datasetId,
			organizationId,
		}: { datasetId: number; organizationId: number } = match.params;

		if (modulesClient) {
			_loadInitialState(organizationId, datasetId);
		}
	}, [modulesClient]);

	useEffect(() => {
		validateFields();
		_checkErrors();
	}, [dataset]);

	useEffect(() => {
		validateFields();
		_checkErrors();
	}, [isLoading]);

	const _loadInitialState = async (orgId: number, datasetId: number) => {
		let loadedProducts = await OrganizationAPI.getOrganizationProducts({
			organizationId: orgId,
		});
		setProducts(loadedProducts);
		let loadedOrganization = await OrganizationAPI.getOrganization({
			id: orgId,
		});
		setOrganization(loadedOrganization);

		let loadedDatasetNames = await DatasetAPI.getDatasetNames({
			organizationId: orgId,
		});

		if (datasetsClient) {
			setDatasetPurposes(await datasetsClient.getDatasetPurposeTypes());
		}

		// Editing Dataset
		if (editingDataset && modulesClient) {
			try {
				let loadedDataset = await DatasetAPI.getOrganizationDataset(
					orgId,
					datasetId
				);
				let loadedModules = await modulesClient.getDatasetModules(
					orgId,
					datasetId
				);
				await _loadProductModules(
					match.params.organizationId,
					_productTypeMapping(loadedDataset.productId),
					false
				);
				setDataset(loadedDataset);
				setInitialModules(loadedModules);
				const _productModulesDefinition = await modulesClient.getProductModules(
					orgId,
					_productTypeMapping(loadedDataset.productId)
				);
				setProductModules(_productModulesDefinition);

				let datasetName = loadedDataset.name
					? loadedDataset.name.toLowerCase()
					: "";

				setDatasetNames(
					loadedDatasetNames.filter(
						(name) => name.toLowerCase() !== datasetName
					)
				);
			} catch (ex) {
				console.log(ex);
			}
		}

		// Creating new dataset
		if (!editingDataset) {
			setDatasetNames(loadedDatasetNames);
		}

		setIsLoading(false);
	};

	const _loadProductModules = async (
		organizationId: number,
		newProductId: number,
		setLoadedModules: boolean
	) => {
		if (modulesClient) {
			let loadedModules = await modulesClient.getProductModules(
				organizationId,
				newProductId
			);

			setProductModules(loadedModules);
			if (setLoadedModules) {
				setFieldsValue({
					moduleId: _convertModulesToIds(
						loadedModules.filter((m) => m.required)
					),
				});
			}
		}
	};

	const _onSelectedConfigVersion = (selectedVersionId: number) => {
		let updatedDataset = dataset;
		dataset.careConfigurationVersionId = selectedVersionId;
		setDataset(updatedDataset);
	};

	const _onTestLanguageChanged = (languages: number[]) => {
		const updatedDataset = {
			...dataset,
			testLanguageIds: languages,
		};
		setDataset(updatedDataset);
	};

	const _getLanguageOptions = (): PickListItemResponseDTO[] => {
		const picklistCode = "Language";
		if (pickListItems === undefined) return [];
		return pickListItems.filter((p) => p.pickListTypeCode === picklistCode);
	};

	const _getVehicleTypeOptions = () => {
		let filteredVehiclePickListItems: PickListItemResponseDTO[] = [];

		/* Note Picklist Ids are being used here as Code uniqueness
		 * is not enforced in the database */
		const vehiclePickListTypeId = 11013; // Vehicle
		const medicalVehiclePicklistId = 15404; // LightMedical
		const updatedDataset = dataset;

		// Get Vehicle Types from Context
		const items: PickListItemResponseDTO[] = pickListItems
			? pickListItems.filter(
					(p: PickListItemResponseDTO) =>
						p.pickListTypeId === vehiclePickListTypeId
			  )
			: [];

		// Filter based on medical vehicle type
		if (items && items.length > 0) {
			if (
				updatedDataset &&
				updatedDataset.productId &&
				updatedDataset.productId === "DriveABLE"
			) {
				filteredVehiclePickListItems = items.filter((f) => {
					return f.id === medicalVehiclePicklistId;
				});

				if (
					updatedDataset.vehicleTypeIds &&
					updatedDataset.vehicleTypeIds.length === 0
				) {
					// Set dataset vehicle type to light - medical by default
					updatedDataset.vehicleTypeIds = [medicalVehiclePicklistId];
					setDataset(updatedDataset);
				}
			} else {
				filteredVehiclePickListItems = items.filter((f) => {
					return f.id !== medicalVehiclePicklistId;
				});
			}
		}
		return filteredVehiclePickListItems;
	};

	const _checkErrors = () => {
		const fieldsErrors = getFieldsError();

		let keys = Object.keys(fieldsErrors);
		if (keys.length <= 0) return;

		// check if any of the values are undefined
		let hasErrors = keys.some(
			(field) =>
				fieldsErrors[field] !== undefined && fieldsErrors[field]!.length > 0
		);
		setFormIsValid(!hasErrors);
	};

	const _generateToastSuccessMessage = (datasetId: number) => {
		if (datasetId === -1) {
			return "Successfully created a dataset";
		}

		return "Successfully updated dataset";
	};

	const _onVehicleTypeChanged = (value: any) => {
		const updatedDataset = {
			...dataset,
			vehicleTypeIds: [...value],
		};
		setDataset(updatedDataset);
	};

	const _onRadioChanged = (e: any) => {
		const updatedDataset = {
			...dataset,
			[e.target.name]: e.target.value,
		};
		setDataset(updatedDataset);
	};

	const _productTypeMapping = (productType: string | undefined) => {
		let productId = 0;
		if (productType === "DriveABLE") {
			productId = 3;
		}
		if (productType === "ExceleRATEV2") {
			productId = 2;
		}
		if (productType === "ExceleRATEV1") {
			productId = 1;
		}

		return productId;
	};

	const _onProductChanged = (value: any) => {
		const productType = _productTypeMapping(value);
		const updatedDataset = {
			...dataset,
			productId: value,
		};

		_loadProductModules(match.params.organizationId, productType, true);
		setDataset(updatedDataset);
	};

	const _onPurposeChanged = (value: any) => {
		let updatedDataset = {
			...dataset,
		};
		updatedDataset.datasetPurposeTypeId = value;
		setDataset(updatedDataset);
	};

	const _convertModulesToIds = (modules: Module[] | undefined): number[] => {
		if (modules === undefined) return [];

		return modules.map((module) => {
			return module.id;
		});
	};

	const _submitDatasetForm = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (dataset.isActive) {
			_submitDataset(dataset);
		} else {
			setRedactMessageOpen(true);
		}
	};

	const _submitDataset = async (datasetToSubmit: DatasetDTO) => {
		const organizationId = +match.params.organizationId;
		const datasetId = +match.params.datasetId;

		if (organization.automaticDataLock && !dataset.automaticDataLock) {
			toast.error(
				"Current organization setting doesn't allow for no data lock"
			);
			return;
		}
		if (
			organization.automaticClientRedaction &&
			!dataset.automaticClientRedaction
		) {
			toast.error(
				"Current organization setting doesn't allow for no redaction"
			);
			return;
		}

		let upsertRequest: UpsertDatasetRequest = { ...datasetToSubmit };
		upsertRequest.name = getFieldValue("name");
		upsertRequest.productId = getFieldValue("productId");
		upsertRequest.modifiedOn = moment().toISOString();
		if (datasetId === -1) {
			const DEFAULT_RISK_LEVEL = 3;
			upsertRequest.riskLevel = DEFAULT_RISK_LEVEL;
		}

		setSaving(true);
		const toastSuccessMessage = _generateToastSuccessMessage(datasetId);

		try {
			let res = await DatasetAPI.upsertOrganizationDataset(
				organizationId,
				upsertRequest
			);

			let currentModules: number[] = getFieldValue("moduleId");

			if (currentModules !== undefined) {
				let modulesToAdd: ModuleDatasetXRef[] = currentModules
					.filter(
						(m) =>
							initialModules.findIndex(
								(initialModule) => initialModule.id === m
							) === -1
					)
					.map((module) => {
						let moduleRef = new ModuleDatasetXRef();
						moduleRef.datasetId = res.id!;
						moduleRef.moduleId = module;
						return moduleRef;
					});
				let modulesToRemove: ModuleDatasetXRef[] = initialModules
					.filter(
						(m) =>
							currentModules.findIndex(
								(currentModule) => currentModule === m.id
							) === -1
					)
					.map((module) => {
						let moduleRef = new ModuleDatasetXRef();
						moduleRef.datasetId = res.id!;
						moduleRef.moduleId = module.id;
						return moduleRef;
					});
				if (modulesClient && modulesToAdd.length > 0) {
					try {
						await modulesClient.addModuleDataset(
							modulesToAdd,
							organizationId.toString()
						);
					} catch {
						toast.error("Something went wrong adding modules!");
						setSaving(false);
					}
				}
				if (modulesClient && modulesToRemove.length > 0) {
					try {
						await modulesClient.deleteModuleDataset(
							modulesToRemove,
							organizationId.toString()
						);
						toast.success(toastSuccessMessage);
					} catch {
						toast.error("Something went wrong removing modules!");
						setSaving(false);
					}
				}
				toast.success(toastSuccessMessage);
			}

			updateCurrentDataset(res);
			history.push(
				`/admin/organizations/${organizationId}/datasets/${res.id}/details`
			);
		} catch (e) {
			console.error(e);
			toast.error("Something went wrong!");
			setSaving(false);
		}
	};

	const _submitDatasetRedactConfirm = () => {
		const updatedDataset = { ...dataset };
		updatedDataset.redacted = true;
		_submitDataset(updatedDataset);
	};
	if (isLoading) return <Spinner />;

	return isLoading ? (
		<Spinner />
	) : (
		<Page title="Dataset Details">
			<Form
				onSubmit={(e: any) => _submitDatasetForm(e)}
				onChange={_checkErrors}
			>
				<div className="container-fluid">
					<div className="row">
						<div className="col-6 form-group">
							<label>Name</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									{getFieldDecorator("name", {
										rules: [
											{
												required: true,
												message: "Enter a dataset name",
												whitespace: true,
											},
											{
												validator: (
													rule: any,
													value: string,
													callback: (msg?: string) => void
												) => {
													datasetNames.findIndex(
														(name) => name === value.toLowerCase()
													) > -1
														? callback("Dataset name already exists")
														: callback();
												},
											},
										],
										initialValue: dataset.name || "",
									})(
										<Input placeholder="Enter a dataset name" maxLength={50} />
									)}
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-6 form-group">
							<label>Purpose</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									{getFieldDecorator("datasetPurposeTypeId", {
										rules: [
											{
												required: true,
												message: "Select a purpose",
											},
										],
										initialValue: dataset.datasetPurposeTypeId,
									})(
										<Select
											disabled={editingDataset}
											onChange={_onPurposeChanged}
										>
											{datasetPurposes.map((purpose) => {
												return (
													<Select.Option key={purpose.id} value={purpose.id}>
														{purpose.name}
													</Select.Option>
												);
											})}
										</Select>
									)}
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-6 form-group">
							<label>Products</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									{getFieldDecorator("productId", {
										rules: [
											{
												required: true,
												message: "Select a product",
											},
										],
										initialValue: dataset.productId,
									})(
										<Select
											disabled={editingDataset}
											onChange={_onProductChanged}
										>
											{products.map((product) => {
												return (
													<Select.Option key={product.id} value={product.id}>
														{product.name}
													</Select.Option>
												);
											})}
										</Select>
									)}
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					{dataset.productId &&
						(dataset.productId === "ExceleRATEV2" ||
							dataset.productId === "DriveABLE") && (
							<div className="row">
								<div className="col-6 form-group">
									<label>Modules</label>
									<Form.Item>
										<AuthorizationWrapper
											type="disable"
											permissions={[AdminPermissions.EditDataset]}
										>
											{getFieldDecorator("moduleId", {
												rules: [
													{
														required: true,
														message: "Select a module",
													},
												],
												initialValue: _convertModulesToIds(initialModules),
											})(
												<Select
													mode="multiple"
													allowClear={false}
													onChange={() => {
														validateFields();
														_checkErrors();
													}}
												>
													{productModules &&
														productModules.map((module) => {
															return (
																<Select.Option
																	key={module.id}
																	value={module.id}
																>
																	{module.name}
																</Select.Option>
															);
														})}
												</Select>
											)}
										</AuthorizationWrapper>
									</Form.Item>
								</div>
							</div>
						)}
					{dataset.productId &&
						(dataset.productId === "ExceleRATEV2" ||
							dataset.productId === "DriveABLE") && (
							<CareConfigurationSelector
								careConfigurationId={dataset.careConfigurationId}
								careConfigurationVersionId={dataset.careConfigurationVersionId}
								onSelectedCareConfigurationVersionChange={
									_onSelectedConfigVersion
								}
							/>
						)}
					{dataset.productId === "ExceleRATEV1" && (
						<div className="row">
							<div className="col-6 form-group">
								<label>Cognitive test language</label>
								<Form.Item>
									<AuthorizationWrapper
										type="disable"
										permissions={[AdminPermissions.EditDataset]}
									>
										{getFieldDecorator("testLanguageIds", {
											rules: [{ required: true, message: "Choose a language" }],
											initialValue: dataset.testLanguageIds,
										})(
											<Select mode="multiple" onChange={_onTestLanguageChanged}>
												{_getLanguageOptions().map((languageOption) => {
													return (
														<Select.Option
															key={languageOption.id}
															value={languageOption.id}
														>
															{languageOption.description}
														</Select.Option>
													);
												})}
											</Select>
										)}
									</AuthorizationWrapper>
								</Form.Item>
							</div>
						</div>
					)}
					<div className="row">
						<div className="col-6 form-group">
							<label>Vehicle Types</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									{getFieldDecorator("Vehicle", {
										rules: [
											{
												required: true,
												message: "Choose a Vehicle Type",
											},
										],
										initialValue: dataset.vehicleTypeIds,
									})(
										<Select
											onChange={(value) => {
												_onVehicleTypeChanged(value);
											}}
											mode="multiple"
										>
											{_getVehicleTypeOptions().map((vehicleType) => {
												return (
													<Select.Option
														key={vehicleType.id}
														value={vehicleType.id}
													>
														{vehicleType.code}
													</Select.Option>
												);
											})}
										</Select>
									)}
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-3 form-group">
							<label>Automatic participant info redaction</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									<Radio.Group
										value={dataset.automaticClientRedaction}
										name="automaticClientRedaction"
										onChange={(e) => _onRadioChanged(e)}
									>
										<Radio value={ClientAutoRedactionState.Inherit}>
											Inherit
										</Radio>
										<Radio value={ClientAutoRedactionState.None}>None</Radio>
										<Radio value={ClientAutoRedactionState.Timed}>Timed</Radio>
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
						<div className="col-3 form-group">
							<label>Maximum days left until redaction</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									{getFieldDecorator("daysUntilRedaction", {
										rules: [
											{
												min: 1,
												max: organization.maximumDaysUntilRedaction
													? organization.maximumDaysUntilRedaction
													: 10000,
											},
										],
									})(
										<InputNumber
											disabled={
												dataset.automaticClientRedaction ===
													ClientAutoRedactionState.None ||
												dataset.automaticClientRedaction ===
													ClientAutoRedactionState.Inherit
											}
											precision={0}
										/>
									)}
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-3 form-group">
							<label>Status</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									<Radio.Group
										value={dataset.isActive}
										name="isActive"
										onChange={(e) => _onRadioChanged(e)}
									>
										<Radio value={true} checked={dataset.isActive}>
											Active
										</Radio>
										<Radio value={false} checked={!dataset.isActive}>
											Inactive
										</Radio>
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-3 form-group">
							<label>Mark as training?</label>
							<Form.Item>
								<Radio.Group
									value={dataset.training}
									name="training"
									onChange={(e) => _onRadioChanged(e)}
								>
									<Radio value={false} checked={!dataset.training}>
										No
									</Radio>
									<Radio value={true} checked={dataset.training}>
										Yes
									</Radio>
								</Radio.Group>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col">
							<DatasetContext.Consumer>
								{(context) => (
									<div>
										<AuthorizationWrapper
											type="hide"
											permissions={[AdminPermissions.EditDataset]}
										>
											<Button
												type="primary"
												disabled={!formIsValid}
												htmlType="submit"
												loading={saving}
											>
												Save
											</Button>
										</AuthorizationWrapper>
										<ModalOverlay
											isOpen={redactMessageOpen}
											modalSize="modal-lg"
											headerChildren="Dataset redaction confirmation"
											children="This dataset has been set to inactive. Would you also like to REDACT ALL PII from this dataset? You will be asked to confirm a second time."
											confirmButtonChildren="Yes, redact information"
											cancelButtonChildren="No, save normally"
											cancelButtonAction={() => _submitDataset(dataset)}
											confirmButtonAction={() => {
												setRedactConfirmationOpen(true);
												setRedactMessageOpen(false);
											}}
											onRequestClose={() => {
												setRedactMessageOpen(false);
											}}
										/>

										<ModalOverlay
											isOpen={redactConfirmationOpen}
											modalSize="modal-lg"
											headerChildren="Dataset redaction confirmation"
											children="Are you sure you want to REDACT ALL PII from this dataset? There is no going back"
											confirmButtonChildren="YES, I'm sure"
											cancelButtonChildren="NO, save normally"
											cancelButtonAction={() => _submitDataset(dataset)}
											confirmButtonAction={() => {
												_submitDatasetRedactConfirm();
											}}
											onRequestClose={() => {
												setRedactConfirmationOpen(false);
											}}
										/>
									</div>
								)}
							</DatasetContext.Consumer>
						</div>
					</div>
				</div>
			</Form>
		</Page>
	);
};

export default Form.create<IProps>()(OrganizationDatasetDetails);
